import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import {
  Loader2,
  Send,
  Users,
  CheckCircle2,
  XCircle,
  Search,
  X,
  Bell,
  BellOff,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { useSendNotificationMutation, useGetCustomersQuery } from "state/api";
import {
  formatNotificationResult,
  isValidNotificationImageUrl,
} from "utils/notification";

const hasPushToken = (user) => Boolean(user?.fcmToken?.trim());

const SendNotificationDialog = ({
  open,
  onOpenChange,
  initialUserIds = [],
  initialUsers = [],
  title: dialogTitle = "Send Push Notification",
  description = "Send a push notification to mobile app users",
}) => {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [sendToAll, setSendToAll] = useState(initialUserIds.length === 0);
  const [selectedUserIds, setSelectedUserIds] = useState(initialUserIds);
  const [message, setMessage] = useState({ type: "", text: "" });
  const [isUserDialogOpen, setIsUserDialogOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [userPage, setUserPage] = useState(1);

  const [sendNotification, { isLoading }] = useSendNotificationMutation();
  const { data: customersResponse, isLoading: isLoadingUsers, isFetching } =
    useGetCustomersQuery(
      {
        page: userPage,
        limit: 50,
        search: userSearchTerm.trim() || undefined,
      },
      { skip: !open && !isUserDialogOpen }
    );

  const pageUsers = customersResponse?.users || customersResponse?.data || [];
  const pagination = customersResponse?.pagination;
  const selectedUsersFromPage = pageUsers.filter((user) =>
    selectedUserIds.includes(user._id)
  );
  const selectedUsers =
    selectedUsersFromPage.length > 0
      ? selectedUsersFromPage
      : initialUsers.filter((user) => selectedUserIds.includes(user._id));

  useEffect(() => {
    if (!open) return;
    setSelectedUserIds(initialUserIds);
    setSendToAll(initialUserIds.length === 0);
    setMessage({ type: "", text: "" });
  }, [open, initialUserIds]);

  useEffect(() => {
    setUserPage(1);
  }, [userSearchTerm]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage({ type: "", text: "" });

    if (!title.trim() || !body.trim()) {
      setMessage({ type: "error", text: "Title and body are required" });
      return;
    }

    if (!sendToAll && selectedUserIds.length === 0) {
      setMessage({
        type: "error",
        text: "Select at least one user or enable send to all users",
      });
      return;
    }

    if (!isValidNotificationImageUrl(imageUrl)) {
      setMessage({
        type: "error",
        text: "Image URL must be a valid http or https link. Leave it blank if you do not need an image.",
      });
      return;
    }

    try {
      const result = await sendNotification({
        title: title.trim(),
        body: body.trim(),
        ...(imageUrl.trim() && { imageUrl: imageUrl.trim() }),
        ...(sendToAll ? {} : { userIds: selectedUserIds }),
      }).unwrap();

      if (result.success && (result.sentCount || 0) > 0) {
        setMessage({
          type: "success",
          text: formatNotificationResult(result),
        });
        setTitle("");
        setBody("");
        setImageUrl("");
        if (initialUserIds.length === 0) {
          setSelectedUserIds([]);
          setSendToAll(true);
        }
      } else {
        setMessage({
          type: "error",
          text: formatNotificationResult(result),
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text:
          error?.data?.message ||
          error?.message ||
          "An error occurred while sending notification",
      });
    }
  };

  const toggleUserSelection = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription>{description}</DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {message.text && (
              <div
                className={`p-3 rounded-lg flex items-start gap-2 text-sm ${
                  message.type === "success"
                    ? "bg-green-50 text-green-800 border border-green-200"
                    : "bg-red-50 text-red-800 border border-red-200"
                }`}
              >
                {message.type === "success" ? (
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                ) : (
                  <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                )}
                <span className="flex-1">{message.text}</span>
                <button type="button" onClick={() => setMessage({ type: "", text: "" })}>
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}

            <div className="space-y-2">
              <label htmlFor="push-title" className="text-sm font-medium">
                Title <span className="text-red-500">*</span>
              </label>
              <Input
                id="push-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Notification title"
                required
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="push-body" className="text-sm font-medium">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="push-body"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Notification message"
                required
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="push-image" className="text-sm font-medium">
                Image URL (optional)
              </label>
              <Input
                id="push-image"
                value={imageUrl}
                onChange={(e) => setImageUrl(e.target.value)}
                placeholder="https://example.com/image.jpg"
              />
              <p className="text-xs text-muted-foreground">
                Optional. Must be a full https:// or http:// link. Leave blank to send text only.
              </p>
            </div>

            {initialUserIds.length === 0 && (
              <div className="flex items-center justify-between p-3 border rounded-lg">
                <div>
                  <p className="text-sm font-medium">Send to all users</p>
                  <p className="text-xs text-muted-foreground">
                    Broadcast to every user with a registered push token
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendToAll}
                    onChange={(e) => {
                      setSendToAll(e.target.checked);
                      if (e.target.checked) setSelectedUserIds([]);
                    }}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full" />
                </label>
              </div>
            )}

            {!sendToAll && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium">
                    Recipients: {selectedUserIds.length}
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setIsUserDialogOpen(true)}
                  >
                    <Users className="h-4 w-4 mr-1.5" />
                    Select Users
                  </Button>
                </div>
                {selectedUsers.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {selectedUsers.map((user) => (
                      <span
                        key={user._id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 text-xs"
                      >
                        {user.name || user.phone || user.email || "User"}
                        <button
                          type="button"
                          onClick={() => toggleUserSelection(user._id)}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  isLoading ||
                  !title.trim() ||
                  !body.trim() ||
                  (!sendToAll && selectedUserIds.length === 0)
                }
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Send Notification
                  </>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={isUserDialogOpen} onOpenChange={setIsUserDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle>Select Users</DialogTitle>
            <DialogDescription>
              Search users and select who should receive this notification
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, or phone..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex-1 overflow-auto border rounded-lg">
            {isLoadingUsers || isFetching ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : pageUsers.length === 0 ? (
              <div className="p-8 text-center text-sm text-muted-foreground">No users found</div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12" />
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Push</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pageUsers.map((user) => {
                    const selected = selectedUserIds.includes(user._id);
                    const pushEnabled = hasPushToken(user);
                    return (
                      <TableRow
                        key={user._id}
                        className={`cursor-pointer ${selected ? "bg-blue-50" : ""}`}
                        onClick={() => toggleUserSelection(user._id)}
                      >
                        <TableCell>
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                              selected ? "bg-blue-600 border-blue-600" : "border-gray-300"
                            }`}
                          >
                            {selected && <CheckCircle2 className="h-4 w-4 text-white" />}
                          </div>
                        </TableCell>
                        <TableCell>{user.name || "N/A"}</TableCell>
                        <TableCell>{user.phone || user.phoneNumber || "N/A"}</TableCell>
                        <TableCell>
                          {pushEnabled ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700">
                              <Bell className="h-3.5 w-3.5" />
                              Enabled
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                              <BellOff className="h-3.5 w-3.5" />
                              No token
                            </span>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </div>

          {pagination && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                Page {pagination.currentPage} of {pagination.totalPages || 1} ·{" "}
                {pagination.totalUsers} users
              </span>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!pagination.hasPrevPage}
                  onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!pagination.hasNextPage}
                  onClick={() => setUserPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button type="button" onClick={() => setIsUserDialogOpen(false)}>
              Done ({selectedUserIds.length})
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SendNotificationDialog;
